import { defaultsFromSchema } from './schema';

export type PresetKind = 'process' | 'filament' | 'printer';

export interface Preset {
  id: string;
  kind: PresetKind;
  /** Exact name written into *_settings_id on export */
  name: string;
  description?: string;
  values: Record<string, string | number | boolean>;
}

const base = defaultsFromSchema();

/** Process-owned keys (exclude filament material keys) so apply does not clobber filament. */
const PROCESS_BASE_KEYS = [
  'layer_height',
  'initial_layer_print_height',
  'wall_loops',
  'top_shell_layers',
  'bottom_shell_layers',
  'sparse_infill_density',
  'sparse_infill_pattern',
  'outer_wall_speed',
  'inner_wall_speed',
  'sparse_infill_speed',
  'top_surface_speed',
  'enable_support',
  'support_type',
  'brim_type',
  'brim_width',
  'outer_wall_line_width',
  'inner_wall_line_width',
  'sparse_infill_line_width',
] as const;

function processBase(): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const k of PROCESS_BASE_KEYS) {
    if (k in base) out[k] = base[k];
  }
  return out;
}

function processPreset(
  id: string,
  name: string,
  patch: Record<string, string | number | boolean>,
  description?: string
): Preset {
  return { id, kind: 'process', name, description, values: { ...processBase(), ...patch } };
}

/** Orca / Bambu Lab P1S 0.4 nozzle process profiles */
export const PROCESS_PRESETS: Preset[] = [
  processPreset('proc-0.08-extra-fine', '0.08mm Extra Fine @BBL P1S', {
    layer_height: 0.08,
    initial_layer_print_height: 0.16,
    top_shell_layers: 9,
    bottom_shell_layers: 7,
    outer_wall_speed: 120,
    inner_wall_speed: 200,
  }),
  processPreset('proc-0.12-fine', '0.12mm Fine @BBL P1S', {
    layer_height: 0.12,
    initial_layer_print_height: 0.2,
    top_shell_layers: 7,
    bottom_shell_layers: 5,
    outer_wall_speed: 150,
    inner_wall_speed: 250,
  }),
  processPreset('proc-0.16-optimal', '0.16mm Optimal @BBL P1S', {
    layer_height: 0.16,
    initial_layer_print_height: 0.2,
    top_shell_layers: 5,
    bottom_shell_layers: 4,
  }),
  processPreset('proc-0.20-standard', '0.20mm Standard @BBL P1S', {
    layer_height: 0.2,
    initial_layer_print_height: 0.2,
    top_shell_layers: 5,
    bottom_shell_layers: 3,
  }),
  processPreset('proc-0.24-draft', '0.24mm Draft @BBL P1S', {
    layer_height: 0.24,
    initial_layer_print_height: 0.24,
    top_shell_layers: 4,
    bottom_shell_layers: 3,
    outer_wall_speed: 200,
  }),
  processPreset('proc-0.28-extra-draft', '0.28mm Extra Draft @BBL P1S', {
    layer_height: 0.28,
    initial_layer_print_height: 0.28,
    top_shell_layers: 3,
    bottom_shell_layers: 2,
    outer_wall_speed: 200,
    inner_wall_speed: 300,
  }),
  processPreset(
    'proc-strength',
    'Strength @BBL P1S',
    {
      layer_height: 0.2,
      initial_layer_print_height: 0.2,
      wall_loops: 4,
      sparse_infill_density: 40,
      sparse_infill_pattern: 'gyroid',
      top_shell_layers: 6,
      bottom_shell_layers: 5,
    },
    'Thicker walls + denser gyroid infill'
  ),
];

export const FILAMENT_PRESETS: Preset[] = [
  {
    id: 'fil-pla-basic',
    kind: 'filament',
    name: 'Bambu PLA Basic',
    values: {
      filament_type: 'PLA',
      filament_colour: '#E8C547',
      nozzle_temperature: 220,
      nozzle_temperature_initial_layer: 220,
      hot_plate_temp: 55,
      hot_plate_temp_initial_layer: 55,
      fan_min_speed: 80,
      fan_max_speed: 100,
      filament_flow_ratio: 0.98,
      filament_density: 1.26,
      filament_max_volumetric_speed: 21,
    },
  },
  {
    id: 'fil-pla-matte',
    kind: 'filament',
    name: 'Bambu PLA Matte',
    values: {
      filament_type: 'PLA',
      filament_colour: '#6FBF4A',
      nozzle_temperature: 220,
      nozzle_temperature_initial_layer: 220,
      hot_plate_temp: 55,
      hot_plate_temp_initial_layer: 55,
      fan_min_speed: 80,
      fan_max_speed: 100,
      filament_flow_ratio: 0.98,
      filament_density: 1.31,
      filament_max_volumetric_speed: 21,
    },
  },
  {
    id: 'fil-petg-hf',
    kind: 'filament',
    name: 'Bambu PETG HF',
    values: {
      filament_type: 'PETG',
      filament_colour: '#2BBBAD',
      nozzle_temperature: 260,
      nozzle_temperature_initial_layer: 260,
      hot_plate_temp: 80,
      hot_plate_temp_initial_layer: 80,
      fan_min_speed: 20,
      fan_max_speed: 50,
      filament_flow_ratio: 0.95,
      filament_density: 1.25,
      filament_max_volumetric_speed: 20,
    },
  },
  {
    id: 'fil-petg-basic',
    kind: 'filament',
    name: 'Bambu PETG Basic',
    values: {
      filament_type: 'PETG',
      filament_colour: '#1A9B8E',
      nozzle_temperature: 255,
      nozzle_temperature_initial_layer: 255,
      hot_plate_temp: 80,
      hot_plate_temp_initial_layer: 80,
      fan_min_speed: 30,
      fan_max_speed: 60,
      filament_flow_ratio: 0.95,
      filament_density: 1.27,
      filament_max_volumetric_speed: 12,
    },
  },
  {
    id: 'fil-tpu-95a',
    kind: 'filament',
    name: 'Bambu TPU 95A',
    values: {
      filament_type: 'TPU',
      filament_colour: '#3A3A3A',
      nozzle_temperature: 230,
      nozzle_temperature_initial_layer: 230,
      hot_plate_temp: 35,
      hot_plate_temp_initial_layer: 35,
      fan_min_speed: 80,
      fan_max_speed: 100,
      filament_flow_ratio: 1.0,
      filament_density: 1.22,
      filament_max_volumetric_speed: 3.2,
      outer_wall_speed: 30,
      inner_wall_speed: 40,
    },
  },
  {
    id: 'fil-abs',
    kind: 'filament',
    name: 'Bambu ABS',
    values: {
      filament_type: 'ABS',
      filament_colour: '#F2EDE3',
      nozzle_temperature: 270,
      nozzle_temperature_initial_layer: 270,
      hot_plate_temp: 90,
      hot_plate_temp_initial_layer: 90,
      fan_min_speed: 10,
      fan_max_speed: 40,
      filament_flow_ratio: 0.95,
      filament_density: 1.04,
      filament_max_volumetric_speed: 16,
    },
  },
  {
    id: 'fil-asa',
    kind: 'filament',
    name: 'Bambu ASA',
    values: {
      filament_type: 'ASA',
      filament_colour: '#D9C7A3',
      nozzle_temperature: 270,
      nozzle_temperature_initial_layer: 270,
      hot_plate_temp: 90,
      hot_plate_temp_initial_layer: 90,
      fan_min_speed: 10,
      fan_max_speed: 40,
      filament_flow_ratio: 0.94,
      filament_density: 1.05,
      filament_max_volumetric_speed: 14,
    },
  },
];

export const PRINTER_PRESETS: Preset[] = [
  {
    id: 'printer-p1s-0.4',
    kind: 'printer',
    name: 'Bambu Lab P1S 0.4 nozzle',
    description: '256×256×256 · 0.4 mm nozzle',
    values: {},
  },
];

/** @deprecated use PROCESS_PRESETS — kept for any leftover imports */
export const BUILTIN_PRESETS: Preset[] = PROCESS_PRESETS;

export const DEFAULT_PROCESS_ID = 'proc-0.20-standard';
export const DEFAULT_FILAMENT_ID = 'fil-pla-basic';
export const DEFAULT_PRINTER_ID = 'printer-p1s-0.4';

const LS_USER = 'p1s-slicer-user-presets';
const LS_ACTIVE = 'p1s-slicer-active-presets';

export function loadUserPresets(): Preset[] {
  try {
    const raw = localStorage.getItem(LS_USER);
    if (!raw) return [];
    return (JSON.parse(raw) as Preset[]).map((p) => ({
      ...p,
      kind: p.kind || 'process',
    }));
  } catch {
    return [];
  }
}

export function saveUserPreset(preset: Preset): Preset[] {
  const all = loadUserPresets().filter((p) => p.id !== preset.id);
  all.push(preset);
  localStorage.setItem(LS_USER, JSON.stringify(all));
  return all;
}

export function renameUserPreset(id: string, name: string): Preset[] {
  const all = loadUserPresets().map((p) => (p.id === id ? { ...p, name } : p));
  localStorage.setItem(LS_USER, JSON.stringify(all));
  return all;
}

export function deleteUserPreset(id: string): Preset[] {
  const all = loadUserPresets().filter((p) => p.id !== id);
  localStorage.setItem(LS_USER, JSON.stringify(all));
  return all;
}

export interface ActivePresetIds {
  processId: string;
  filamentId: string;
  printerId: string;
}

export function loadActivePresetIds(): ActivePresetIds {
  try {
    const raw = localStorage.getItem(LS_ACTIVE);
    if (raw) {
      const j = JSON.parse(raw) as Partial<ActivePresetIds>;
      return {
        processId: j.processId || DEFAULT_PROCESS_ID,
        filamentId: j.filamentId || DEFAULT_FILAMENT_ID,
        printerId: j.printerId || DEFAULT_PRINTER_ID,
      };
    }
  } catch { /* ignore */ }
  return {
    processId: DEFAULT_PROCESS_ID,
    filamentId: DEFAULT_FILAMENT_ID,
    printerId: DEFAULT_PRINTER_ID,
  };
}

export function saveActivePresetIds(ids: ActivePresetIds) {
  try {
    localStorage.setItem(LS_ACTIVE, JSON.stringify(ids));
  } catch { /* ignore */ }
}

export function findProcessPreset(id: string, user: Preset[] = []): Preset | undefined {
  return PROCESS_PRESETS.find((p) => p.id === id) || user.find((p) => p.id === id && p.kind === 'process');
}

export function findFilamentPreset(id: string, user: Preset[] = []): Preset | undefined {
  return FILAMENT_PRESETS.find((p) => p.id === id) || user.find((p) => p.id === id && p.kind === 'filament');
}

export function findPrinterPreset(id: string): Preset | undefined {
  return PRINTER_PRESETS.find((p) => p.id === id);
}

/** Keys owned by process vs filament for dirty detection */
export const PROCESS_KEYS = new Set(
  Object.keys(PROCESS_PRESETS.find((p) => p.id === DEFAULT_PROCESS_ID)!.values).filter(
    (k) => !(k in FILAMENT_PRESETS[0].values) || ['layer_height', 'wall_loops', 'sparse_infill_density', 'sparse_infill_pattern', 'top_shell_layers', 'bottom_shell_layers', 'outer_wall_speed', 'inner_wall_speed', 'initial_layer_print_height'].includes(k)
  )
);

export function processKeysOf(preset: Preset): string[] {
  return Object.keys(preset.values);
}

export function filamentKeysOf(preset: Preset): string[] {
  return Object.keys(preset.values);
}

export function valuesEqual(a: string | number | boolean | undefined, b: string | number | boolean | undefined) {
  if (typeof a === 'number' || typeof b === 'number') {
    return Math.abs(Number(a) - Number(b)) < 1e-6;
  }
  return a === b;
}

export function dirtyKeys(
  settings: Record<string, string | number | boolean>,
  preset: Preset | undefined,
  keys: string[]
): string[] {
  if (!preset) return [];
  return keys.filter((k) => k in preset.values && !valuesEqual(settings[k], preset.values[k]));
}
